import React, { useState, useMemo } from 'react';
import { arrayOf, bool, object, string } from 'prop-types';
import { Flex } from 'rebass/styled-components';

import { isMobile } from '../../../utils/animationHelper';
import CommonGroupRow from './commonGroupRow';
import { getComponentToRender } from '../../../utils/targetHelpers';
import PromoCardContainer from '../promoCardContainer';
import { areAllPromoCards } from '../promoCardContainer/utils';
import { createFlexTemplate, createBoxTemplate } from './createTemplate';

/* eslint-disable react/prop-types */
function renderGroupRow({
  columnsToRender,
  heading,
  isHeadingVisible,
  groupRowTitle,
  setGroupRowTitle,
  isWhiteBackground,
  headingPosition,
  restrictContent,
  cushionContent,
  contentAlignment,
  isBlackBackground,
  backgroundStyle,
  columnMargin,
  page,
}) {
  const isPromoCardContainer = areAllPromoCards(columnsToRender);

  return (
    <CommonGroupRow
      isWhiteBackground={isWhiteBackground}
      heading={heading}
      headingPosition={headingPosition}
      isHeadingVisible={isHeadingVisible}
      setGroupRowTitle={setGroupRowTitle}
      restrictContent={restrictContent}
      cushionContent={cushionContent}
      isBlackBackground={isBlackBackground}
      backgroundStyle={backgroundStyle}
    >
      {isPromoCardContainer ? (
        <PromoCardContainer
          columns={columnsToRender}
          page={page}
          groupRowTitle={groupRowTitle}
          restrictContent={restrictContent}
        />
      ) : (
        columnsToRender &&
        columnsToRender.map(item =>
          createFlexTemplate(
            item,
            columnsToRender,
            columnMargin,
            contentAlignment,
            groupRowTitle,
            page,
            restrictContent,
          ),
        )
      )}
    </CommonGroupRow>
  );
}

function renderFlexRow({
  columnsToRender,
  heading,
  isHeadingVisible,
  groupRowTitle,
  setGroupRowTitle,
  isWhiteBackground,
  headingPosition,
  restrictContent,
  cushionContent,
  isBlackBackground,
  backgroundStyle,
  page,
}) {
  return (
    <CommonGroupRow
      isWhiteBackground={isWhiteBackground}
      heading={heading}
      headingPosition={headingPosition}
      setGroupRowTitle={setGroupRowTitle}
      headingBlock
      isHeadingVisible={isHeadingVisible}
      restrictContent={restrictContent}
      cushionContent={cushionContent}
      isBlackBackground={isBlackBackground}
      backgroundStyle={backgroundStyle}
    >
      <Flex width={1}>
        {columnsToRender &&
          columnsToRender.map(item =>
            createBoxTemplate(
              item,
              columnsToRender,
              groupRowTitle,
              page,
              restrictContent,
            ),
          )}
      </Flex>
    </CommonGroupRow>
  );
}

const defaultIdentifier = 'groupRow';
const defaultContentAlignment = 'left';
const defaultHeadingPosition = 'center';
const GroupRow = ({
  identifier = defaultIdentifier,
  columns,
  heading = null,
  isHeadingVisible = false,
  isAnimationEnabled = false,
  headingPosition = defaultHeadingPosition,
  isWhiteBackground = false,
  restrictContent = true,
  cushionContent = false,
  isLastRow = false,
  isFirstRow = true,
  targetAudience = null,
  contentAlignment = defaultContentAlignment,
  isBlackBackground = false,
  backgroundStyle = null,
  noTopSpacing = false,
  noBottomSpacing = false,
  page,
}) => {
  const [groupRowTitle, setGroupRowTitle] = useState('');

  const columnMargin = useMemo(() => {
    if (noTopSpacing || noBottomSpacing) {
      return {
        // eslint-disable-next-line no-nested-ternary
        pt: [noTopSpacing ? 0 : isFirstRow ? [5, 11] : 5],
        pb: [noBottomSpacing ? 0 : 5],
      };
    }
    return { py: [5] };
  }, [noTopSpacing, isFirstRow]);

  let renderFunction = renderGroupRow;
  if (identifier === 'flexRow') {
    renderFunction = renderFlexRow;
  }
  const columnsToRender = columns?.reduce(
    (acc, component) =>
      getComponentToRender(acc, component, targetAudience, page),
    [],
  );

  const shouldAnimate = !isMobile() && isAnimationEnabled;
  return renderFunction({
    shouldAnimate,
    columnsToRender,
    heading,
    isHeadingVisible,
    groupRowTitle,
    setGroupRowTitle,
    isWhiteBackground,
    headingPosition,
    restrictContent,
    cushionContent,
    isFirstRow,
    isLastRow,
    contentAlignment,
    isBlackBackground,
    backgroundStyle,
    columnMargin,
    page,
  });
};

GroupRow.displayName = 'GroupRow';

GroupRow.propTypes = {
  identifier: string,
  columns: arrayOf(object).isRequired,
  heading: object, // TODO: Rich text content shape
  isHeadingVisible: bool,
  isAnimationEnabled: bool,
  headingPosition: string,
  isWhiteBackground: bool,
  isLastRow: bool,
  isFirstRow: bool,
  id: string.isRequired,
  targetAudience: string,
  restrictContent: bool,
  cushionContent: bool,
  contentAlignment: string,
  isBlackBackground: bool,
  noTopSpacing: bool,
  noBottomSpacing: bool,
  page: object.isRequired,
  backgroundStyle: object,
};

export default GroupRow;
